import { format } from 'date-fns';

import type { InventoryRow, InventoryStatut } from '../types/inventoryRow';

export const POC_INVENTORY_CLOSURE_EVENT = 'poc-inventory-closure-changed';

const STORAGE_KEY = 'poc-app-inventory-closures-v1';

export interface InventoryClosureRecord {
  reference: string;
  statut: Extract<InventoryStatut, 'Clôturé'>;
  dateComptage: string;
  dateMiseAJour: string;
  quantiteTheorique: number;
  quantiteComptee: number;
  ecart: number;
  sommePaHt: number;
  sommePvTtc: number;
  ecartPaHt: number;
  ecartPvTtc: number;
  nombreArticles: number;
  closedBy: string;
  closedAt: string;
}

interface StorageShape {
  byReference: Record<string, InventoryClosureRecord>;
}

const emptyShape = (): StorageShape => ({ byReference: {} });

function readStorage(): StorageShape {
  if (typeof window === 'undefined') {
    return emptyShape();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyShape();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyShape();
    }

    const obj = parsed as { byReference?: unknown };

    if (!obj.byReference || typeof obj.byReference !== 'object') {
      return emptyShape();
    }

    return { byReference: obj.byReference as StorageShape['byReference'] };
  } catch {
    return emptyShape();
  }
}

function writeStorage(data: StorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(POC_INVENTORY_CLOSURE_EVENT));
}

export function loadInventoryClosure(reference: string): InventoryClosureRecord | undefined {
  return readStorage().byReference[reference];
}

export function isInventoryClosed(reference: string): boolean {
  return loadInventoryClosure(reference) !== undefined;
}

export interface InventoryClosureSummaryInput {
  reference: string;
  quantiteTheorique: number;
  quantiteComptee: number;
  sommePaHt: number;
  sommePvTtc: number;
  ecartPaHt: number;
  ecartPvTtc: number;
  nombreArticles: number;
  closedBy: string;
}

export function persistInventoryClosure(summary: InventoryClosureSummaryInput): InventoryClosureRecord {
  const today = format(new Date(), 'yyyy-MM-dd');
  const record: InventoryClosureRecord = {
    reference: summary.reference,
    statut: 'Clôturé',
    dateComptage: today,
    dateMiseAJour: today,
    quantiteTheorique: summary.quantiteTheorique,
    quantiteComptee: summary.quantiteComptee,
    ecart: summary.quantiteComptee - summary.quantiteTheorique,
    sommePaHt: summary.sommePaHt,
    sommePvTtc: summary.sommePvTtc,
    ecartPaHt: summary.ecartPaHt,
    ecartPvTtc: summary.ecartPvTtc,
    nombreArticles: summary.nombreArticles,
    closedBy: summary.closedBy,
    closedAt: new Date().toISOString(),
  };

  const data = readStorage();

  data.byReference[summary.reference] = record;
  writeStorage(data);

  return record;
}

export function applyClosureToInventoryRow(row: InventoryRow): InventoryRow {
  const closure = loadInventoryClosure(row.reference);

  if (!closure) {
    return row;
  }

  return {
    ...row,
    statut: closure.statut,
    dateComptage: closure.dateComptage,
    dateMiseAJour: closure.dateMiseAJour,
    quantiteTheorique: closure.quantiteTheorique,
    quantiteComptee: closure.quantiteComptee,
    ecart: closure.ecart,
    sommePaHt: closure.sommePaHt,
    sommePvTtc: closure.sommePvTtc,
    nombreArticles: closure.nombreArticles,
  };
}
